import React from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { Button } from '@/features/ui/components/Button'; // Use shadcn alias path
import { logger } from '@/features/help/utils/logger';

interface CancelSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isProcessing: boolean;
  currentPeriodEnd: Date | null;
}

const formatDate = (date: Date | null): string => {
  if (!date) return 'the end of your current billing period';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(date);
};

export const CancelSubscriptionModal: React.FC<CancelSubscriptionModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isProcessing,
  currentPeriodEnd,
}) => {
  if (!isOpen) return null;

  const handleConfirm = () => {
    logger.debug('CancelSubscriptionModal', 'Confirm button clicked');
    onConfirm();
  };

  const handleClose = () => {
    logger.debug('CancelSubscriptionModal', 'Close/Keep Plan button clicked');
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 transition-opacity duration-300 ease-in-out"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-white rounded-lg shadow-xl p-6 mx-4 w-full max-w-md relative transform transition-all duration-300 ease-in-out scale-100">
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          aria-label="Close modal"
          disabled={isProcessing}
        >
          <X size={20} />
        </button>

        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-red-100 sm:h-10 sm:w-10">
            <AlertTriangle className="h-6 w-6 text-red-600" aria-hidden="true" />
          </div>
          <div className="mt-0 text-left flex-1">
            <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
              Cancel Subscription
            </h3>
            <div className="mt-2">
              <p className="text-sm text-gray-600">
                Are you sure you want to cancel your Pro subscription? Your plan benefits will
                remain active until {formatDate(currentPeriodEnd)}.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row-reverse sm:gap-3">
          <Button
            variant="destructive" // Destructive variant for the main action
            onClick={handleConfirm}
            disabled={isProcessing}
            className="w-full sm:w-auto mb-2 sm:mb-0"
          >
            {isProcessing ? 'Processing...' : 'Yes, Cancel Subscription'}
          </Button>
          <Button
            variant="outline" // Outline/secondary for the keep action
            onClick={handleClose}
            disabled={isProcessing}
            className="w-full sm:w-auto"
          >
            Keep Plan
          </Button>
        </div>
      </div>
    </div>
  );
};
